/**
 * DigitalCardPreview — premium heritage-style tribute card.
 *
 * Share options:
 *   • Copy message (clipboard)
 *   • Share via WhatsApp (wa.me link — user picks recipient)
 *   • Share via email (mailto)
 *   • Download (scaffolded — browser print to PDF)
 *
 * WhatsApp auto-send is NOT used. We open wa.me which lets the user choose their recipient.
 * No message is "sent automatically" — this is clearly disclosed in the UI.
 */
import React, { useRef, useState } from 'react';
import { Copy, Share2, Download, MessageCircle, Mail, Check, Flame, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  defaultCardMessage, makeShareMessage, makeWhatsAppUrl, REMINDER_TYPE_LABELS,
  formatReminderDate,
} from '../../utils/reminders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_THEMES = {
  birthday: { bg: '#2C4A3B', accent: '#c9a961', text: '#fff', icon: '🎂', emoji: '✦' },
  death_anniversary: { bg: '#1a1a2e', accent: '#c9a961', text: '#f5f0e8', icon: '🕯️', emoji: '✦' },
  remembrance_day: { bg: '#1a1a2e', accent: '#c9a961', text: '#f5f0e8', icon: '🕯️', emoji: '✦' },
  wedding_anniversary: { bg: '#3b2f2f', accent: '#c9a961', text: '#fff', icon: '💛', emoji: '✦' },
  custom: { bg: '#2C4A3B', accent: '#c9a961', text: '#fff', icon: '📅', emoji: '✦' },
};

/**
 * @param {{
 *   reminder: object,
 *   onClose?: () => void,
 * }} props
 */
export function DigitalCardPreview({ reminder, onClose }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);

  if (!reminder) return null;

  const rtype = reminder.reminder_type || 'custom';
  const theme = CARD_THEMES[rtype] || CARD_THEMES.custom;
  const personName = reminder.person_name || '';
  const message = defaultCardMessage(reminder);
  const dateStr = formatReminderDate(reminder.date_month, reminder.date_day, reminder.date_year);
  const typeLabel = REMINDER_TYPE_LABELS[rtype] || 'A family moment';
  const imageUrl = reminder.person_image_url
    ? resolveAssetUrl(BACKEND_URL, reminder.person_image_url)
    : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(makeShareMessage(reminder));
      setCopied(true);
      toast.success('Message copied to clipboard.');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const handleWhatsApp = () => {
    window.open(makeWhatsAppUrl(reminder), '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    const subj = encodeURIComponent(`${typeLabel} — ${personName}`);
    const body = encodeURIComponent(makeShareMessage(reminder));
    window.open(`mailto:?subject=${subj}&body=${body}`);
  };

  const handleDownload = () => {
    toast.message('Download tip', {
      description: 'Use your browser\'s "Print to PDF" (Ctrl+P / Cmd+P) to save this card.',
      duration: 6000,
    });
    setTimeout(() => window.print(), 300);
  };

  return (
    <div className="space-y-6" data-testid="digital-card-preview">
      {/* The card itself */}
      <div
        ref={cardRef}
        className="mx-auto max-w-sm overflow-hidden rounded-2xl shadow-[0_16px_48px_-12px_rgba(0,0,0,0.30)]"
        style={{ background: theme.bg }}
        data-testid="tribute-card"
      >
        {/* Top ornament bar */}
        <div style={{ background: theme.accent, height: '4px' }} />

        <div className="px-8 pb-8 pt-7" style={{ color: theme.text }}>
          {/* Person photo or initials */}
          <div className="mb-5 flex justify-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={personName}
                className="h-20 w-20 rounded-full object-cover"
                style={{ border: `3px solid ${theme.accent}` }}
              />
            ) : personName ? (
              <div
                className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-serif"
                style={{ background: `${theme.accent}22`, border: `2px solid ${theme.accent}55` }}
              >
                <span style={{ color: theme.accent }}>
                  {personName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                </span>
              </div>
            ) : (
              <div
                className="flex h-20 w-20 items-center justify-center rounded-full text-2xl"
                style={{ background: `${theme.accent}22` }}
              >
                <span>{theme.icon}</span>
              </div>
            )}
          </div>

          {/* Occasion */}
          <p
            className="mb-1 text-center text-[10px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: theme.accent }}
          >
            {typeLabel}
          </p>

          {/* Name */}
          {personName && (
            <h3
              className="mb-1 text-center font-serif text-2xl font-light"
              style={{ color: theme.text }}
            >
              {personName}
            </h3>
          )}

          {/* Date */}
          <p className="mb-6 text-center text-xs opacity-60">{dateStr}</p>

          {/* Ornament rule */}
          <div className="mb-6 flex items-center gap-3">
            <div className="flex-1" style={{ height: '1px', background: `${theme.accent}40` }} />
            <span style={{ color: theme.accent, fontSize: '10px' }}>{theme.emoji}</span>
            <div className="flex-1" style={{ height: '1px', background: `${theme.accent}40` }} />
          </div>

          {/* Message */}
          <p
            className="text-center font-serif text-[0.95rem] leading-[1.75] italic"
            style={{ color: theme.text, opacity: 0.9 }}
          >
            &ldquo;{message}&rdquo;
          </p>

          {/* Bottom branding */}
          <p
            className="mt-8 text-center text-[9px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: theme.accent, opacity: 0.6 }}
          >
            MyLegacyBridge
          </p>
        </div>

        {/* Bottom accent bar */}
        <div style={{ background: theme.accent, height: '2px' }} />
      </div>

      {/* Share actions */}
      <div className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60 text-center">
          Share this card
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={handleCopy}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Copy message'}
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full gap-2 border-green-300/60 text-green-700 hover:bg-green-50/80"
            onClick={handleWhatsApp}
            title="Opens WhatsApp — you choose the recipient"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full gap-2"
            onClick={handleEmail}
          >
            <Mail className="h-3.5 w-3.5" />
            Email
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full gap-2 opacity-70"
            onClick={handleDownload}
          >
            <Download className="h-3.5 w-3.5" />
            Download / Print
          </Button>
        </div>

        <p className="text-center text-[10px] text-muted-foreground/60">
          WhatsApp share opens your WhatsApp app — you choose who to send it to.
        </p>
      </div>
    </div>
  );
}

export default DigitalCardPreview;
